'use client';

import { useCallback, useRef } from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';

/**
 * Props for a modal backdrop that dismisses on a click *away* from the sheet —
 * and only on that.
 *
 * 🚨 A bare `onClick` with `e.target === e.currentTarget` is not enough, and the
 * gap is not theoretical. `click` fires on the nearest common ancestor of the
 * mousedown and the mouseup, so a text selection that starts inside the sheet
 * and finishes past its edge lands a click whose target IS the backdrop —
 * identical, to that check, to someone deliberately clicking away. A user filmed
 * themselves highlighting a quote description, dragging out of the modal,
 * releasing the button, and watching the quote vanish.
 *
 * So a dismiss requires a press that both BEGINS and ENDS on the backdrop.
 * Checking only the start still loses the mirror-image drag (press on the
 * backdrop, release inside), which is how someone selecting *towards* the sheet
 * loses their work instead.
 *
 * Touch is covered for free: browsers fire compatibility mousedown/mouseup/click
 * after touchend, in that order, on the same element.
 */
export function useBackdropDismiss(onDismiss: () => void) {
  const downOnBackdrop = useRef(false);
  const upOnBackdrop = useRef(false);

  const onMouseDown = useCallback((e: ReactMouseEvent) => {
    downOnBackdrop.current = e.target === e.currentTarget;
  }, []);

  const onMouseUp = useCallback((e: ReactMouseEvent) => {
    upOnBackdrop.current = e.target === e.currentTarget;
  }, []);

  const onClick = useCallback(
    (e: ReactMouseEvent) => {
      const cleanPress = downOnBackdrop.current && upOnBackdrop.current;
      downOnBackdrop.current = false;
      upOnBackdrop.current = false;
      if (cleanPress && e.target === e.currentTarget) onDismiss();
    },
    [onDismiss],
  );

  return { onMouseDown, onMouseUp, onClick };
}
